import { firstViolation } from './graphqlViolations';

/**
 * YOJO-3123 [self-registered staff-domain email gets analyst] — staff roles no longer come from an
 * email address. A work-address Google login that has NO staff account is refused by the backend on
 * `registerLogin` with `extensions.violations[0] = { message, status: 403 }`.
 *
 * The refusal travels as its own error type so the login screen can show the backend's sentence as
 * written, and tell it apart from every other sign-in failure.
 */

// The other side of this contract is yojo-api
// `src/main/java/yojo/flow/user/identity/StaffAccountRequiredException.java`: it holds the same
// sentence, and the two must change together.
export const STAFF_ACCOUNT_REQUIRED_MESSAGE = 'Ask an admin to add you in the care portal.';

export class StaffAccessRefusedError extends Error {
  constructor() {
    super(STAFF_ACCOUNT_REQUIRED_MESSAGE);
    this.name = 'StaffAccessRefusedError';
  }
}

/**
 * The ONE place that decides whether a failed `registerLogin` is the staff refusal: status 403 AND
 * exactly the backend's sentence. `registerLogin` has other 403s (not authenticated, not an identity,
 * no identity id) and none of them is this. When the backend sends a marker, change this line.
 */
export function staffAccessRefusal(err: unknown): StaffAccessRefusedError | null {
  const violation = firstViolation(err);
  const refused = violation?.status === 403 && violation.message === STAFF_ACCOUNT_REQUIRED_MESSAGE;
  return refused ? new StaffAccessRefusedError() : null;
}

/**
 * The browser-side check in `signInWithGoogle`: a Google account outside the staff domains. Its own
 * type, so the login screen recognises it without matching on the sentence.
 */
export class StaffEmailDomainError extends Error {
  constructor() {
    super('Please use your authorized Lumina or Yojo email to sign in');
    this.name = 'StaffEmailDomainError';
  }
}

/**
 * The refused sentence is also kept in sessionStorage. After the sign-out, a sibling query that is
 * refused too makes apollo.ts hard-redirect to `/login?error=session_expired`; that reload drops the
 * in-memory refusal and would leave a misleading "session expired" on screen.
 *
 * Storage can throw (private windows, blocked storage): every access is guarded, and the in-memory
 * refusal works without it.
 */
export const STAFF_ACCESS_REFUSAL_STORAGE_KEY = 'yojo_staff_access_refusal';

export function rememberStaffAccessRefusal(message: string): void {
  try {
    sessionStorage.setItem(STAFF_ACCESS_REFUSAL_STORAGE_KEY, message);
  } catch {
    // No storage: the refusal is still shown from memory
  }
}

/**
 * Only ever the refusal sentence itself: the login screen renders what this returns, so any other
 * stored value (stale, tampered, or written by something else) is treated as nothing stored.
 */
export function storedStaffAccessRefusal(): string | null {
  try {
    const stored = sessionStorage.getItem(STAFF_ACCESS_REFUSAL_STORAGE_KEY)?.trim();
    return stored === STAFF_ACCOUNT_REQUIRED_MESSAGE ? stored : null;
  } catch {
    return null;
  }
}

export function clearStaffAccessRefusal(): void {
  try {
    sessionStorage.removeItem(STAFF_ACCESS_REFUSAL_STORAGE_KEY);
  } catch {
    // No storage: nothing was kept
  }
}
